// Internal-staff company mapping admin. Sync Shopify B2B companies, provision client logins mapped
// to their CompanyContact/CompanyLocation, and assign reps to companies.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import StoreSwitcher from '../components/shell/StoreSwitcher.jsx';
import {
  fetchCompanies,
  syncCompanies,
  setCompanyTrusted,
  bulkSetActive,
  bulkSetOwners,
  fetchAssignableOwners,
  backfillSpend,
  fetchCatalogs,
  createCompany,
  bulkInvite,
} from '../api/companies.js';
import { useStoreContext } from '../stores/storeContext.js';
import { storeName, storeShort } from '../lib/stores.js';
import { fmtMoney } from '../lib/matrix.js';
import { useAuthStore } from '../stores/authStore.js';
import { can, CAP } from '../lib/access.js';
import { Chip, Btn, AssignRepForm, ClientLogins } from '../components/ClientAccess.jsx';

const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';
const moneyMap = (obj) =>
  Object.entries(obj || {})
    .filter(([, v]) => v)
    .map(([c, v]) => fmtMoney(v, c))
    .join(' · ') || '—';


// --- Create a brand-new B2B company (staff) ---
function NewCompanyModal({ defaultStore, onClose, onCreated }) {
  const [store, setStore] = useState(defaultStore || 'A');
  const [companyName, setCompanyName] = useState('');
  const [contact, setContact] = useState({ email: '', firstName: '', lastName: '' });
  const [locationName, setLocationName] = useState('');
  const [addr, setAddr] = useState({ address1: '', address2: '', city: '', zoneCode: '', zip: '', countryCode: '' });
  const [catalogs, setCatalogs] = useState([]);
  const [catalogId, setCatalogId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // { inviteUrl, name, catalogAssigned, catalogError, simulated, message }

  useEffect(() => {
    setCatalogs([]);
    setCatalogId('');
    fetchCatalogs(store).then(setCatalogs).catch(() => setCatalogs([]));
  }, [store]);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const chosen = catalogs.find((c) => c.id === catalogId);
      const res = await createCompany({
        store,
        companyName: companyName.trim(),
        contact: { email: contact.email.trim(), firstName: contact.firstName.trim(), lastName: contact.lastName.trim() },
        location: { name: locationName.trim() || undefined, address: addr },
        catalogId: catalogId || undefined,
        currencyCode: chosen?.currency || undefined,
      });
      setDone(res);
      if (!res.simulated) onCreated?.();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = companyName.trim() && /.+@.+\..+/.test(contact.email);
  const input = 'w-full rounded-md border border-line px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="mt-10 w-full max-w-lg rounded-lg border border-line bg-surface p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-text-primary">New B2B company</h2>
          <button onClick={onClose} className="rounded px-2 py-1 text-sm text-text-muted hover:bg-surface-raised">✕</button>
        </div>

        {done ? (
          <div className="space-y-3 text-sm">
            {done.simulated ? (
              <div className="rounded-md bg-surface-raised px-3 py-2 text-text-secondary">{done.message}</div>
            ) : (
              <>
                <div className="rounded-md bg-success-light px-3 py-2 text-success">
                  Created <span className="font-medium">{done.name}</span> in {storeName(store)}.
                  {done.catalogAssigned ? ' Catalogue assigned.' : done.catalogError ? ` Catalogue not assigned: ${done.catalogError}` : ' No catalogue assigned.'}
                </div>
                {done.inviteUrl && (
                  <div className="rounded-md border border-accent/40 bg-accent-light/50 p-3 text-xs">
                    <div className="mb-1 font-medium text-text-primary">
                      {done.emailSent
                        ? `✓ Login instructions emailed to ${contact.email}. You can also copy the link:`
                        : `⚠ Couldn't email ${contact.email} — copy this link and share it:`}
                    </div>
                    <div className="flex items-center gap-2">
                      <input readOnly value={done.inviteUrl} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1 font-mono text-[11px]" />
                      <button type="button" onClick={() => navigator.clipboard?.writeText(done.inviteUrl)} className="shrink-0 rounded border border-line px-2 py-1 font-medium text-text-secondary hover:bg-surface">Copy</button>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-end">
              <Btn variant="primary" onClick={onClose}>Done</Btn>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              {['A', 'B'].map((s) => (
                <button key={s} onClick={() => setStore(s)} className={`rounded-full px-3 py-1 text-sm ${store === s ? 'bg-accent text-white' : 'bg-surface-raised text-text-secondary'}`}>
                  {storeName(s)}
                </button>
              ))}
            </div>
            <div>
              <label className="text-xs font-medium text-text-muted">Company name *</label>
              <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={input} placeholder="e.g. Acme Menswear Pty Ltd" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-3"><label className="text-xs font-medium text-text-muted">Primary contact email *</label>
                <input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={input} placeholder="buyer@acme.com" /></div>
              <div><label className="text-xs font-medium text-text-muted">First name</label>
                <input value={contact.firstName} onChange={(e) => setContact({ ...contact, firstName: e.target.value })} className={input} /></div>
              <div><label className="text-xs font-medium text-text-muted">Last name</label>
                <input value={contact.lastName} onChange={(e) => setContact({ ...contact, lastName: e.target.value })} className={input} /></div>
            </div>
            <div>
              <label className="text-xs font-medium text-text-muted">Pricing catalogue</label>
              <select value={catalogId} onChange={(e) => setCatalogId(e.target.value)} className={input}>
                <option value="">No catalogue (assign later in Shopify)</option>
                {catalogs.map((c) => (
                  <option key={c.id} value={c.id}>{c.title}{c.priceListName ? ` · ${c.priceListName}` : ''}{c.currency ? ` (${c.currency})` : ''}</option>
                ))}
              </select>
              <p className="mt-0.5 text-[11px] text-text-muted">Without a catalogue the client has no wholesale pricing and can't order until one is assigned.</p>
            </div>
            <details className="rounded-md border border-line p-2">
              <summary className="cursor-pointer text-xs font-medium text-text-secondary">Shipping address + location name (optional)</summary>
              <div className="mt-2 space-y-2">
                <input value={locationName} onChange={(e) => setLocationName(e.target.value)} className={input} placeholder="Location name (defaults to company name)" />
                <input value={addr.address1} onChange={(e) => setAddr({ ...addr, address1: e.target.value })} className={input} placeholder="Address line 1" />
                <input value={addr.address2} onChange={(e) => setAddr({ ...addr, address2: e.target.value })} className={input} placeholder="Address line 2" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className={input} placeholder="City" />
                  <input value={addr.zoneCode} onChange={(e) => setAddr({ ...addr, zoneCode: e.target.value })} className={input} placeholder="State/Province code (e.g. NSW)" />
                  <input value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} className={input} placeholder="Postcode" />
                  <input value={addr.countryCode} onChange={(e) => setAddr({ ...addr, countryCode: e.target.value.toUpperCase() })} maxLength={2} className={input} placeholder="Country code (e.g. AU)" />
                </div>
              </div>
            </details>
            {error && <p className="text-sm text-error">{error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <Btn onClick={onClose}>Cancel</Btn>
              <Btn variant="primary" onClick={submit} busy={busy} disabled={!canSubmit}>Create company</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function CompanyCard({ company, onChange, selected, onToggle }) {
  const navigate = useNavigate();
  const [assigning, setAssigning] = useState(false);
  const [trusting, setTrusting] = useState(false);
  const reps = company.repAssignments.map((a) => a.repUser);
  const toggleTrusted = async () => {
    setTrusting(true);
    try {
      await setCompanyTrusted(company.id, !company.isTrusted);
      onChange();
    } finally {
      setTrusting(false);
    }
  };

  return (
    <div className={`rounded-lg border bg-surface p-4 shadow-sm ${selected ? 'border-accent' : 'border-line'}`}>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={!!selected} onChange={onToggle} className="shrink-0" title="Select" />
        <Chip tone="muted">{storeShort(company.store.code)}</Chip>
        <button
          type="button"
          onClick={() => navigate(`/clients/${company.id}`)}
          className="text-sm font-semibold text-text-primary hover:underline"
        >
          {company.name}
        </button>
        {company.isTrusted && <Chip tone="accent">Trusted</Chip>}
        {company.dormant && <Chip tone="muted">Dormant</Chip>}
        {company.isActive === false && <Chip tone="danger">Inactive</Chip>}
        <button
          type="button"
          onClick={toggleTrusted}
          disabled={trusting}
          className="ml-auto text-xs font-medium text-accent hover:underline disabled:opacity-50"
          title="Trusted: orders under the approval ceiling are approved automatically once shipping is confirmed (new-client, over-ceiling and credit-hold orders still go to a person)"
        >
          {company.isTrusted ? 'Untrust' : 'Mark trusted'}
        </button>
      </div>

      {/* Lifetime spend + current outstanding (account health at a glance) */}
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs">
        <span className="text-text-muted">
          Lifetime spend <span className="font-medium text-text-primary">{moneyMap(company.lifetimeSpend)}</span>
        </span>
        <span className="text-text-muted">
          Outstanding{' '}
          <span className={`font-medium ${moneyMap(company.outstanding) !== '—' ? 'text-warning' : 'text-text-primary'}`}>
            {moneyMap(company.outstanding)}
          </span>
        </span>
      </div>

      <div className="mt-3">
        <ClientLogins company={company} onChange={onChange} />
      </div>

      {/* Account owners (primary rep + CS owner) — managed on the client timeline */}
      {(company.repUser || company.csUser) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
          <span className="text-xs font-medium text-text-muted">Owners:</span>
          {company.repUser && <Chip tone="accent">Rep · {company.repUser.name || company.repUser.email}</Chip>}
          {company.csUser && <Chip tone="accent">CS · {company.csUser.name || company.csUser.email}</Chip>}
        </div>
      )}

      {/* Assigned reps */}
      <div className="mt-3 border-t border-line pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-text-muted">Reps:</span>
          {reps.length ? (
            reps.map((r) => <Chip key={r.id}>{r.email}</Chip>)
          ) : (
            <span className="text-xs text-text-muted">none</span>
          )}
          <button
            type="button"
            onClick={() => setAssigning((a) => !a)}
            className="text-xs font-medium text-accent hover:underline"
          >
            {assigning ? 'Close' : '+ Assign rep'}
          </button>
        </div>
        {assigning && (
          <AssignRepForm
            company={company}
            onDone={() => {
              setAssigning(false);
              onChange();
            }}
          />
        )}
      </div>
    </div>
  );
}

function SyncButton({ store, busy, onClick }) {
  return (
    <Btn onClick={onClick} busy={busy}>
      Sync {storeShort(store)}
    </Btn>
  );
}

export default function Clients() {
  const activeStore = useStoreContext((s) => s.activeStore); // scopes the list to the top-bar store
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active'); // active | inactive | all
  const [repFilter, setRepFilter] = useState(''); // '' = all reps; a rep id; or '__none__' = unassigned
  const [query, setQuery] = useState('');
  const [syncing, setSyncing] = useState(null);
  const [msg, setMsg] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [owners, setOwners] = useState({ reps: [], staff: [] });
  const [bulkBusy, setBulkBusy] = useState('');
  const [showNew, setShowNew] = useState(false);
  const user = useAuthStore((s) => s.user);

  const load = () => {
    setLoading(true);
    fetchCompanies()
      .then(setCompanies)
      .catch(() => setCompanies([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => {
    fetchAssignableOwners().then(setOwners).catch(() => {});
  }, []);

  const toggleSel = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const clearSel = () => setSelected(new Set());

  const runBulk = async (label, fn) => {
    setBulkBusy(label);
    setMsg('');
    try {
      const ids = [...selected];
      const r = await fn(ids);
      setMsg(`${r.updated} of ${ids.length} updated.`);
      clearSel();
      await load();
    } catch (e) {
      setMsg(errMsg(e));
    } finally {
      setBulkBusy('');
    }
  };

  const doBulkInvite = async () => {
    setBulkBusy('invite');
    setMsg('');
    try {
      const r = await bulkInvite([...selected]);
      setMsg(
        `Invites: ${r.emailed} sent` +
          `${r.provisioned ? ` · ${r.provisioned} new login(s) created` : ''}` +
          `${r.failed ? ` · ${r.failed} failed` : ''}` +
          `${r.skippedRegistered ? ` · ${r.skippedRegistered} already registered` : ''}` +
          `${r.emailed === 0 && r.provisioned === 0 ? ' — no contacts with an email found on the selected companies.' : '.'}`
      );
      clearSel();
      await load();
    } catch (e) {
      setMsg(errMsg(e));
    } finally {
      setBulkBusy('');
    }
  };

  const doBackfill = async () => {
    setBulkBusy('backfill');
    setMsg('Backfilling lifetime spend from Shopify… this can take a minute.');
    try {
      const r = await backfillSpend();
      setMsg(`Lifetime spend backfilled — ${r.matched} companies matched (${r.ordersImported} orders), ${r.noOrders} with no linked orders.`);
      await load();
    } catch (e) {
      setMsg(errMsg(e));
    } finally {
      setBulkBusy('');
    }
  };

  const doSync = async (store) => {
    setSyncing(store);
    setMsg('');
    try {
      const r = await syncCompanies(store);
      setMsg(`Synced ${storeName(store)}: ${r.companies} companies, ${r.locations} locations.`);
      load();
    } catch (e) {
      setMsg(errMsg(e));
    } finally {
      setSyncing(null);
    }
  };

  const needle = query.trim().toLowerCase();
  const matchesQuery = (c) => {
    if (!needle) return true;
    if (c.name?.toLowerCase().includes(needle)) return true;
    return c.locations.some(
      (l) =>
        l.name?.toLowerCase().includes(needle) ||
        l.contacts.some((ct) => (ct.user?.email || '').toLowerCase().includes(needle) || (ct.user?.name || '').toLowerCase().includes(needle))
    );
  };
  // A company's reps = those assigned via RepAssignment, plus its primary rep owner (repUser).
  const repIdsOf = (c) => {
    const ids = new Set((c.repAssignments || []).map((a) => a.repUser?.id).filter(Boolean));
    if (c.repUser?.id) ids.add(c.repUser.id);
    return ids;
  };
  const matchesRep = (c) => {
    if (!repFilter) return true;
    const ids = repIdsOf(c);
    return repFilter === '__none__' ? ids.size === 0 : ids.has(repFilter);
  };
  const shown = companies.filter(
    (c) =>
      (!activeStore || c.store.code === activeStore) &&
      (statusFilter === 'all' || (statusFilter === 'inactive' ? c.isActive === false : c.isActive !== false)) &&
      matchesRep(c) &&
      matchesQuery(c)
  );
  const clientCount = shown.reduce((n, c) => n + c.locations.reduce((m, l) => m + l.contacts.length, 0), 0);

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle="Shopify B2B companies, client logins, and rep assignments."
        actions={
          <>
            <StoreSwitcher />
            {can(user, CAP.MANAGE_CLIENTS) && (
              <Btn variant="primary" onClick={() => setShowNew(true)}>
                New company
              </Btn>
            )}
            {can(user, CAP.MANAGE_SETTINGS) && (
              <Btn onClick={doBackfill} busy={bulkBusy === 'backfill'}>
                Backfill spend
              </Btn>
            )}
            <SyncButton store="A" busy={syncing === 'A'} onClick={() => doSync('A')} />
            <SyncButton store="B" busy={syncing === 'B'} onClick={() => doSync('B')} />
          </>
        }
      />

      {showNew && (
        <NewCompanyModal
          defaultStore={activeStore || 'A'}
          onClose={() => setShowNew(false)}
          onCreated={load}
        />
      )}

      {msg && <div className="mb-4 rounded-md bg-accent-light px-3 py-2 text-sm text-text-secondary">{msg}</div>}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {['active', 'inactive', 'all'].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setStatusFilter(f)}
            className={`rounded-full px-3 py-1 text-sm capitalize transition ${
              statusFilter === f ? 'bg-accent text-white' : 'bg-surface-raised text-text-secondary hover:text-text-primary'
            }`}
          >
            {f}
          </button>
        ))}
        <select
          value={repFilter}
          onChange={(e) => setRepFilter(e.target.value)}
          title="Filter by assigned rep"
          className="rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
        >
          <option value="">All reps</option>
          <option value="__none__">Unassigned</option>
          {owners.reps.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name || u.email}
            </option>
          ))}
        </select>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search company, location, or contact…"
          className="w-64 rounded-md border border-line px-3 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
        />
        {query && (
          <button type="button" onClick={() => setQuery('')} className="text-xs text-text-muted hover:text-text-primary">
            Clear
          </button>
        )}
        <span className="ml-auto text-xs text-text-muted">
          {shown.length} companies · {clientCount} linked logins
        </span>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-accent/40 bg-accent-light/40 p-2.5 text-sm">
          <span className="font-medium text-text-primary">{selected.size} selected</span>
          <button disabled={!!bulkBusy} onClick={() => runBulk('a', (ids) => bulkSetActive(ids, true))} className="rounded-md border border-line bg-surface px-2.5 py-1 text-xs font-medium hover:bg-surface-raised">
            Activate
          </button>
          <button disabled={!!bulkBusy} onClick={() => runBulk('d', (ids) => bulkSetActive(ids, false))} className="rounded-md border border-line bg-surface px-2.5 py-1 text-xs font-medium text-error hover:bg-error-light">
            Deactivate
          </button>
          <button disabled={!!bulkBusy} onClick={doBulkInvite} className="rounded-md border border-line bg-surface px-2.5 py-1 text-xs font-medium text-accent hover:bg-accent-light" title="Create a login from each selected company's Shopify contacts and email them an account-setup invitation">
            {bulkBusy === 'invite' ? 'Sending…' : 'Send invites'}
          </button>
          <select
            value=""
            disabled={!!bulkBusy}
            onChange={(e) => e.target.value && runBulk('r', (ids) => bulkSetOwners(ids, { repUserId: e.target.value === '__clear__' ? null : e.target.value }))}
            className="rounded-md border border-line bg-surface px-2 py-1 text-xs"
          >
            <option value="">Assign rep…</option>
            <option value="__clear__">— clear rep —</option>
            {owners.reps.map((u) => (
              <option key={u.id} value={u.id}>{u.name || u.email}</option>
            ))}
          </select>
          <select
            value=""
            disabled={!!bulkBusy}
            onChange={(e) => e.target.value && runBulk('c', (ids) => bulkSetOwners(ids, { csUserId: e.target.value === '__clear__' ? null : e.target.value }))}
            className="rounded-md border border-line bg-surface px-2 py-1 text-xs"
          >
            <option value="">Assign CS…</option>
            <option value="__clear__">— clear CS —</option>
            {owners.staff.map((u) => (
              <option key={u.id} value={u.id}>{u.name || u.email}</option>
            ))}
          </select>
          <button onClick={clearSel} className="ml-auto text-xs text-text-muted hover:text-text-primary">Clear</button>
          {bulkBusy && <span className="text-xs text-text-muted">Working…</span>}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-text-muted">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">
          No companies in {activeStore ? storeName(activeStore) : 'this store'} yet. Click{' '}
          <span className="font-medium text-text-primary">Sync</span> to pull your Shopify B2B companies.
        </div>
      ) : (
        <div className="space-y-3">
          <label className="flex items-center gap-2 px-1 text-xs text-text-muted">
            <input
              type="checkbox"
              checked={shown.every((c) => selected.has(c.id))}
              onChange={(e) => setSelected(e.target.checked ? new Set(shown.map((c) => c.id)) : new Set())}
            />
            Select all ({shown.length})
          </label>
          {shown.map((c) => (
            <CompanyCard key={c.id} company={c} onChange={load} selected={selected.has(c.id)} onToggle={() => toggleSel(c.id)} />
          ))}
        </div>
      )}
    </>
  );
}
